import React, { useState } from 'react';
import Card, { CardRow } from 'components/styleguide/card/Card';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import InputField from 'components/styleguide/input_field/InputField';
import SelectField from 'components/styleguide/select_field/SelectField';
import Textarea from 'components/styleguide/textarea/Textarea';
import Checkbox from 'components/styleguide/checkbox/Checkbox';
import MonthYearPicker from 'components/styleguide/month_year_picker/MonthYearPicker';
import { useEditableSheet } from 'components/styleguide/sheet_modal/useEditableSheet';
import Button from 'components/styleguide/button/Button';
const styles = require('../onboarding-cards.module.scss');

type Option = { value: string | number; label: string };

interface Experience {
  id?: number;
  position: string;
  company: string;
  contract_type: string;
  category_ids: number[];
  categories: Option[];
  description: string;
  start_month: number;
  start_year: number;
  end_month: number | null;
  end_year: number | null;
  current_position: boolean;
  start_date_decorated: string;
  end_date_decorated: string | null;
}

interface WorkExperienceCardProps {
  experiences: Experience[];
  categories: Option[];
  jobTypes: Option[];
  createUrl: string;
  updateUrl: string;
}

const WorkExperienceCard: React.FC<WorkExperienceCardProps> = ({
  experiences: initialExperiences,
  categories,
  jobTypes,
  createUrl,
  updateUrl,
}) => {
  const [experiences, setExperiences] = useState<Experience[]>(initialExperiences || []);
  const [editingId, setEditingId] = useState<number | null>(null);

  const sheet = useEditableSheet({
    url: createUrl,
    method: 'POST',
    payloadKey: 'professional_experience',
    onSuccess: (_formData, responseData) => {
      if (editingId != null) {
        setExperiences(prev => prev.map(e => e.id === editingId ? responseData.data : e));
      } else {
        setExperiences(prev => [...prev, responseData.data]);
      }
    },
    onDelete: (id: number) => {
      setExperiences(prev => prev.filter(e => e.id !== id));
    },
  });

  const hasAny = experiences.length > 0;
  const tag = hasAny ? undefined : { variant: 'warning' as const, icon: 'circleAlert' as const, children: 'Missing' };

  const toTimestamp = (month: number, year: number): number | null => {
    if (month && year) return new Date(year, month - 1, 10).getTime() / 1000;
    return null;
  };

  const fromTimestamp = (ts: number | null): { month: number; year: number } | null => {
    if (!ts) return null;
    const d = new Date(ts * 1000);
    return { month: d.getMonth() + 1, year: d.getFullYear() };
  };

  const openAdd = () => {
    setEditingId(null);
    sheet.open('Add work experience', {
      position: '',
      company: '',
      contract_type: '',
      category_ids: [],
      description: '',
      start_date: null,
      end_date: null,
      current_position: false,
    }, { url: createUrl, method: 'POST' });
  };

  const openEdit = (exp: Experience) => {
    setEditingId(exp.id);
    sheet.open('Edit work experience', {
      position: exp.position,
      company: exp.company,
      contract_type: exp.contract_type,
      category_ids: exp.category_ids,
      description: exp.description || '',
      start_date: toTimestamp(exp.start_month, exp.start_year),
      end_date: toTimestamp(exp.end_month, exp.end_year),
      current_position: exp.current_position,
    }, { url: `${updateUrl}/${exp.id}`, method: 'PATCH' });
  };

  const handleDelete = (id: number) => {
    sheet.handleDelete(`${updateUrl}/${id}`, id);
  };

  const formatDateRange = (exp: Experience): string => {
    const start = exp.start_date_decorated;
    const end = exp.current_position ? 'Present' : exp.end_date_decorated;
    return [start, end].filter(Boolean).join(' - ');
  };

  const selectedCategories = categories.filter(c =>
    (sheet.formData.category_ids || []).includes(c.value)
  );

  return (
    <>
      <Card
        title="Work experience"
        iconName="graduationCap"
        context="talent"
        tag={tag}
        onAdd={openAdd}
        allowMultiple={true}
      >
        {experiences.map(exp => (
          <CardRow
            key={exp.id}
            context="talent"
            onEdit={() => openEdit(exp)}
            onDelete={() => handleDelete(exp.id)}
          >
            <div className={styles.fieldGridCompact}>
              <p className={styles.inputText}>{exp.position}</p>
              <p className={styles.label}>
                {exp.company} &bull; {formatDateRange(exp)}
              </p>
              {exp.description && (
                <p className={styles.truncatedValue}>
                  {exp.description}
                </p>
              )}
            </div>
          </CardRow>
        ))}
      </Card>

      {sheet.isOpen && (
        <Sheet
          title={sheet.sheetTitle}
          onClose={sheet.close}
          context="talent"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={sheet.handleSave}
        >
          <div className={styles.form}>
            <InputField
              label="Position"
              placeholder="e.g. Full Stack Developer"
              name="position"
              required={true}
              value={sheet.formData.position || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              size="L"
              error={sheet.getError('position')}
            />
            <SelectField
              label="Category"
              name="category_ids"
              placeholder="e.g. UX Designer"
              options={categories}
              value={selectedCategories}
              isMulti
              onChange={(selected) => sheet.handleFormChange('category_ids', (selected || []).map((o: Option) => o.value))}
              size="L"
              required={true}
              error={sheet.getError('category_ids')}
            />
            <InputField
              label="Company"
              placeholder="e.g. Landing.jobs"
              name="company"
              required={true}
              value={sheet.formData.company || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              size="L"
              error={sheet.getError('company')}
            />
            <SelectField
              label="Contract type"
              name="contract_type"
              placeholder="e.g. Full-time"
              options={jobTypes}
              value={jobTypes.find((o: Option) => o.value === sheet.formData.contract_type) || null}
              onChange={(v) => sheet.handleFormChange('contract_type', v?.value)}
              size="L"
              required={true}
              error={sheet.getError('contract_type')}
            />
            <div className={styles.fieldRow}>
              <MonthYearPicker
                label="Start date"
                placeholder="Select date"
                required={true}
                maxValue={fromTimestamp(sheet.formData.end_date)}
                value={fromTimestamp(sheet.formData.start_date)}
                onChange={(val) => sheet.handleFormChange('start_date', val ? toTimestamp(val.month, val.year) : null)}
                error={sheet.getError('start_date')}
              />
              <MonthYearPicker
                label="End date"
                placeholder="Select date"
                required={!sheet.formData.current_position}
                disabled={!!sheet.formData.current_position}
                minValue={fromTimestamp(sheet.formData.start_date)}
                value={fromTimestamp(sheet.formData.end_date)}
                onChange={(val) => sheet.handleFormChange('end_date', val ? toTimestamp(val.month, val.year) : null)}
                error={sheet.getError('end_date')}
              />
            </div>
            <Checkbox
              id="current-position"
              name="current_position"
              label="I currently work in this position"
              checked={!!sheet.formData.current_position}
              onChange={(e) => {
                sheet.handleFormChange('current_position', e.target.checked);
                if (e.target.checked) sheet.handleFormChange('end_date', null);
              }}
            />
            <Textarea
              label="Description"
              name="description"
              placeholder="Describe your responsibilities and achievements"
              defaultValue={sheet.formData.description || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              rows={6}
              error={sheet.getError('description')}
            />
            {editingId && (
              <Button
                type="secondary"
                size="M"
                iconLeft="trash"
                context="talent"
                onClick={() => handleDelete(editingId)}
              >
                Delete experience
              </Button>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
};

export default WorkExperienceCard;
